import React from "react";

const PAYOFF = "se non ti trovano, non esisti";

// GoogleS — standalone rainbow S for use as section divider
export const GoogleS = ({ size = 40 }) => {
  const uid = `gs${Math.random().toString(36).slice(2,6)}`;
  return (
    <svg width={size * 0.65} height={size} viewBox="0 0 65 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={uid} x1="0" y1="5" x2="0" y2="88" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#4285F4" />
          <stop offset="26%" stopColor="#4285F4" />
          <stop offset="26.1%" stopColor="#EA4335" />
          <stop offset="38%" stopColor="#EA4335" />
          <stop offset="38.1%" stopColor="#FBBC04" />
          <stop offset="50%" stopColor="#FBBC04" />
          <stop offset="50.1%" stopColor="#4285F4" />
          <stop offset="62%" stopColor="#4285F4" />
          <stop offset="62.1%" stopColor="#34A853" />
          <stop offset="74%" stopColor="#34A853" />
          <stop offset="74.1%" stopColor="#EA4335" />
          <stop offset="100%" stopColor="#EA4335" />
        </linearGradient>
      </defs>
      <text x="32" y="84" textAnchor="middle" fill={`url(#${uid})`} fontFamily="'Poppins', Arial, sans-serif" fontWeight="900" fontSize="98"
        style={{ WebkitTextStroke: '1px #000', paintOrder: 'stroke fill' }}>S</text>
    </svg>
  );
};

// NickLogo: S (Google rainbow top-to-bottom: blu, rosso, giallo, blu, verde, rosso) + .com (red, small)
export const NickLogo = ({ size = 24, className = "", showPayoff = false, payoffSize }) => {
  const comSize = size * 0.28;
  const uid = `sg${size}${Math.random().toString(36).slice(2,6)}`;
  const pSize = payoffSize || (size >= 72 ? "md" : size >= 48 ? "sm" : "xs");
  return (
    <span className={`inline-flex flex-col items-center ${className}`} data-testid="nick-logo">
      <span className="inline-flex items-baseline" style={{ fontFamily: "'Poppins', sans-serif", lineHeight: 1 }}>
        <svg width={size * 0.65} height={size} viewBox="0 0 65 100" xmlns="http://www.w3.org/2000/svg" style={{ display: 'inline', verticalAlign: 'baseline', marginBottom: '-2px' }}>
          <defs>
            <linearGradient id={uid} x1="0" y1="5" x2="0" y2="88" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#4285F4" />
              <stop offset="16.66%" stopColor="#4285F4" />
              <stop offset="16.67%" stopColor="#EA4335" />
              <stop offset="33.33%" stopColor="#EA4335" />
              <stop offset="33.34%" stopColor="#FBBC04" />
              <stop offset="50%" stopColor="#FBBC04" />
              <stop offset="50.01%" stopColor="#4285F4" />
              <stop offset="66.66%" stopColor="#4285F4" />
              <stop offset="66.67%" stopColor="#34A853" />
              <stop offset="83.33%" stopColor="#34A853" />
              <stop offset="83.34%" stopColor="#EA4335" />
              <stop offset="100%" stopColor="#EA4335" />
            </linearGradient>
          </defs>
          <text x="32" y="84" textAnchor="middle" fill={`url(#${uid})`} fontFamily="'Poppins', Arial, sans-serif" fontWeight="900" fontSize="98"
            style={{ WebkitTextStroke: '1px #000', paintOrder: 'stroke fill' }}>S</text>
        </svg>
        <span style={{ color: "#EA4335", fontWeight: 900, fontSize: `${comSize}px`, letterSpacing: '-0.3px', alignSelf: 'flex-end', marginBottom: '2px', WebkitTextStroke: '1.5px #000', paintOrder: 'stroke fill', textShadow: '2px 2px 0 #000, -2px -2px 0 #000, 2px -2px 0 #000, -2px 2px 0 #000, 0 2px 0 #000, 0 -2px 0 #000, 2px 0 0 #000, -2px 0 0 #000' }}>.com</span>
      </span>
      {showPayoff && <PayoffShine size={pSize} />}
    </span>
  );
};

// Shining payoff text with wave animation
export const PayoffShine = ({ size = "sm" }) => {
  const cls = size === "xs" ? "text-[10px]" : size === "sm" ? "text-sm" : size === "md" ? "text-base" : size === "lg" ? "text-lg" : size === "xl" ? "text-xl" : "text-sm";
  const letters = PAYOFF.split('');
  return (
    <span
      className={`${cls} font-black italic block`}
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      {letters.map((ch, i) => (
        <span
          key={i}
          className="payoff-wave-letter"
          style={{
            display: 'inline-block',
            color: '#fff',
            WebkitTextStroke: '0.5px #000',
            paintOrder: 'stroke fill',
            textShadow: '0 0 12px rgba(255,255,255,0.6), 0 0 24px rgba(255,255,255,0.3), 1px 1px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000',
            animationDelay: `${i * 0.06}s`,
          }}
        >
          {ch === ' ' ? '\u00A0' : ch}
        </span>
      ))}
    </span>
  );
};

// CSS for wave + shine animation — injected once
const ShineStyle = () => (
  <style>{`
    @keyframes payoff-shine {
      0% { background-position: 200% center; }
      100% { background-position: -200% center; }
    }
    @keyframes payoff-wave {
      0%, 100% { transform: translateY(0); text-shadow: 0 0 8px rgba(255,255,255,0.4), 0 0 16px rgba(255,255,255,0.2); }
      50% { transform: translateY(-4px); text-shadow: 0 0 16px rgba(255,255,255,0.8), 0 0 32px rgba(255,255,255,0.4); }
    }
    .payoff-wave-letter {
      animation: payoff-wave 2.5s ease-in-out infinite;
    }
  `}</style>
);

// Full Logo for navbar: Schedabusiness.com + payoff
export const NavLogo = ({ className = "" }) => (
  <div className={`flex items-center gap-2.5 ${className}`} data-testid="nav-logo">
    <ShineStyle />
    <NickLogo size={36} />
    <div className="flex flex-col leading-none">
      <BrandText className="text-sm hidden sm:block" />
      <span className="text-xs sm:text-sm font-black italic mt-1" style={{ fontFamily: "'Poppins', sans-serif", letterSpacing: '0.3px' }}>
        <span className="text-gray-500">se non ti </span>
        <span className="font-black text-white" style={{ textShadow: '0 0 12px rgba(255,255,255,0.6), 0 0 24px rgba(255,255,255,0.3)' }}>trovano</span>
        <span className="text-gray-500">, non </span>
        <span className="font-black text-white" style={{ textShadow: '0 0 12px rgba(255,255,255,0.6), 0 0 24px rgba(255,255,255,0.3)' }}>esisti</span>
      </span>
    </div>
  </div>
);

// Full Logo standalone (for special uses)
export const FullLogo = ({ className = "", size = "lg", showPayoff = true }) => {
  const sizes = {
    sm: { nick: 44, text: "text-sm", payoff: "xs" },
    md: { nick: 60, text: "text-lg", payoff: "sm" },
    lg: { nick: 80, text: "text-2xl", payoff: "md" },
    xl: { nick: 110, text: "text-4xl", payoff: "lg" },
  };
  const sz = sizes[size] || sizes.lg;
  return (
    <div className={`flex flex-col items-center ${className}`} data-testid="full-logo">
      <ShineStyle />
      <NickLogo size={sz.nick} />
      <BrandText className={sz.text} />
      {showPayoff && <PayoffShine size={sz.payoff} />}
    </div>
  );
};

// Brand text: Schedabusiness.com in Google colors
const BrandText = ({ className = "" }) => (
  <span className={`${className} font-black tracking-tight`} style={{ fontFamily: "'Poppins', sans-serif", lineHeight: 1.1 }}>
    <span style={{ color: "#4285F4" }}>S</span>
    <span style={{ color: "#EA4335" }}>ch</span>
    <span style={{ color: "#FBBC04" }}>ed</span>
    <span style={{ color: "#4285F4" }}>a</span>
    <span style={{ color: "#34A853" }}>bu</span>
    <span style={{ color: "#EA4335" }}>si</span>
    <span style={{ color: "#FBBC04" }}>ne</span>
    <span style={{ color: "#4285F4" }}>ss</span>
    <span style={{ color: "#EA4335" }}>.com</span>
  </span>
);

export default FullLogo;
